import { z } from 'zod';
import { DiscoEnum } from './wallet-kyc-schema';

const AutoCompleteOptionSchema = (message: string) =>
  z.object(
    {
      id: z.string(),
      name: z.string(),
    },
    { message }
  );

const newAddressSchema = z.object({
  street: z.string({ message: 'Street is required' }).trim(),
  street_line_2: z.string({ message: 'enter a valid street' }).optional(),
  city: z.string({ message: 'city is required' }).trim(),
  state: AutoCompleteOptionSchema('state is required'),
  country: AutoCompleteOptionSchema('Country is required'),
  postal_code: z
    .string({ message: 'Postal code is required' })
    .trim()
    .optional(),
  lga: z
    .string({ message: 'Local government area is required' })
    .trim()
    .optional(),
});

const walletIndividualKycSchema = z
  .object({
    customer_id: z.number().optional(),
    first_name: z
      .string({ message: 'First name is required' })
      .min(1, { message: 'First name is required' }),
    last_name: z
      .string({ message: 'Last name is required' })
      .min(1, { message: 'Last name is required' }),
    phone: z
      .string({ message: 'Valid phone number is required' })
      .trim()
      .min(1, 'Phone number is required'),
    dob: z
      .string({ message: 'valid Date of Birth is required' })
      .trim()
      .min(1, 'Date of birth is required'),
    addressChoice: z.enum(['saved', 'new']),
    savedAddressId: z.string({
      message: 'Select one of your available addresses',
    }),
    newAddress: newAddressSchema,
    nin: z
      .string({ message: 'valid NIN is required' })
      .trim()
      .length(11, 'NIN must be 11 digits')
      .regex(/^\d+$/, 'NIN must contain only numbers'),
    bvn: z
      .string({ message: 'valid BVN is required' })
      .trim()
      .length(11, 'BVN must be 11 digits')
      .regex(/^\d+$/, 'BVN must contain only numbers'),
    meter_number: z.string('meter number is required').trim().optional(),
    disco_code: DiscoEnum.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.addressChoice === 'saved') {
      if (!data.savedAddressId.trim() || isNaN(Number(data.savedAddressId))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Select a saved address',
          path: ['savedAddressId'],
        });
      }
      return;
    }

    if (!data.newAddress.street.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Street is required',
        path: ['newAddress', 'street'],
      });
    }

    if (!data.newAddress.country?.id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Country is required',
        path: ['newAddress', 'country'],
      });
    }

    if (!data.newAddress.state?.id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'State is required',
        path: ['newAddress', 'state'],
      });
    }

    if (!data.newAddress.city.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'City is required',
        path: ['newAddress', 'city'],
      });
    }
  });

export type WalletIndividualKyc = z.infer<typeof walletIndividualKycSchema>;

export default walletIndividualKycSchema;
